import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/lib/i18n";
import AuthLayout from "@/components/layout/AuthLayout";
import { GoogleButton } from "@/components/GoogleButton";
import { Loader2 } from "lucide-react";

export default function Login() {
  const { t } = useT();
  const { setCtx } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/api/auth/login", { email, password });
      setCtx(data);
      navigate(data.user.onboarded ? "/" : "/onboarding", { replace: true });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthLayout>
      <h1 className="text-2xl font-bold tracking-tight text-slate-900">{t("auth.login.title")}</h1>
      <p className="mt-1.5 text-sm text-slate-500">{t("auth.login.subtitle")}</p>

      <form onSubmit={submit} className="mt-8 space-y-4" data-testid="login-form">
        {error && (
          <div className="rounded-lg border border-rose-200 bg-rose-50 px-3.5 py-2.5 text-sm text-rose-700" data-testid="login-error">
            {error}
          </div>
        )}
        <div>
          <label className="mb-1.5 block text-sm font-medium text-slate-700">{t("auth.email")}</label>
          <input
            type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
            data-testid="login-email-input" placeholder="vous@entreprise.com"
            className="w-full rounded-lg border border-slate-300 bg-white px-3.5 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30"
          />
        </div>
        <div>
          <div className="mb-1.5 flex items-center justify-between">
            <label className="block text-sm font-medium text-slate-700">{t("auth.password")}</label>
            <Link to="/forgot-password" data-testid="forgot-password-link" className="text-sm font-medium text-primary hover:underline">
              {t("auth.forgot")}
            </Link>
          </div>
          <input
            type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
            data-testid="login-password-input" placeholder="••••••••"
            className="w-full rounded-lg border border-slate-300 bg-white px-3.5 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30"
          />
        </div>
        <button
          type="submit" disabled={busy} data-testid="login-submit-button"
          className="flex w-full items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-blue-700 disabled:opacity-60"
        >
          {busy && <Loader2 className="h-4 w-4 animate-spin" />}
          {t("auth.login.cta")}
        </button>
      </form>

      <div className="my-6 flex items-center gap-3">
        <div className="h-px flex-1 bg-slate-200" />
        <span className="text-xs uppercase tracking-wider text-slate-400">{t("auth.or")}</span>
        <div className="h-px flex-1 bg-slate-200" />
      </div>
      <GoogleButton label={t("auth.google")} />

      <p className="mt-8 text-center text-sm text-slate-500">
        {t("auth.no_account")}{" "}
        <Link to="/register" data-testid="go-register-link" className="font-semibold text-primary hover:underline">
          {t("auth.register")}
        </Link>
      </p>
    </AuthLayout>
  );
}
